/**
 * DEFERRED SEND: leave ENCRYPTED files for someone who is offline right now.
 *
 * The sender can be a GUEST (guest-first): the recipient's `ENVAC1-…` address
 * is enough. Each file is encrypted HERE, in the browser, for each of the
 * recipient's trusted devices (ephemeral ECDH → AES-GCM) and is
 * deposited in their mailbox; the relay only sees opaque envelopes. The recipient
 * collects and decrypts them on their Account screen when they come back online.
 */

import { createRoute, Link } from "@tanstack/solid-router";
import { createSignal, For, Show } from "solid-js";
import { Clock3, Lock, Search } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { ACCOUNT_ADDRESS_PREFIX, parseAccountAddress, type RemoteDevice } from "@enviar/core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { PageBackground } from "@/components/ui/page-background";
import { TopBar } from "@/components/ui/top-bar";
import { loadLocalAccount, sendDeferredFiles } from "@/lib/mailbox/account";
import { fetchDevices, MailboxApiError } from "@/lib/mailbox/client";

const panelClass =
  "flex w-full flex-col gap-4 rounded-[var(--radius-2xl)] border border-border bg-surface/70 p-6 shadow-md backdrop-blur";

const inputClass =
  "h-11 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type Phase = "compose" | "sending" | "done";

function DeferredPage() {
  const { t } = useI18n();
  const [phase, setPhase] = createSignal<Phase>("compose");
  const [address, setAddress] = createSignal("");
  const [recipientDevices, setRecipientDevices] = createSignal<RemoteDevice[]>();
  const [files, setFiles] = createSignal<File[]>([]);
  const [error, setError] = createSignal<string>();
  const [sentCount, setSentCount] = createSignal(0);

  const errorMessage = (err: unknown): string => {
    if (err instanceof MailboxApiError) {
      switch (err.kind) {
        case "not-found": {
          return t(msg`No mailbox found for that address. Double-check it.`);
        }
        case "rate-limited": {
          return t(msg`Too many attempts — try again in a few minutes.`);
        }
        case "invalid": {
          return t(msg`The recipient's mailbox is full or rejected the delivery.`);
        }
        default: {
          return t(msg`The mailbox service is unreachable right now.`);
        }
      }
    }
    return t(msg`Something went wrong. Try again.`);
  };

  const lookup = () => {
    setError(undefined);
    setRecipientDevices(undefined);
    const accountId = parseAccountAddress(address());
    if (accountId === null) {
      setError(t(msg`That does not look like an Enviar address (ENVAC1-…).`));
      return;
    }
    void fetchDevices(accountId)
      .then(setRecipientDevices)
      .catch((err: unknown) => setError(errorMessage(err)));
  };

  const send = () => {
    setError(undefined);
    setPhase("sending");
    void (async () => {
      const account = await loadLocalAccount();
      const result = await sendDeferredFiles(
        address(),
        files(),
        account ? { from: account.deviceName } : undefined,
      );
      switch (result.kind) {
        case "sent": {
          setSentCount(result.envelopes);
          setPhase("done");
          return;
        }
        case "bad-address": {
          setError(t(msg`That does not look like an Enviar address (ENVAC1-…).`));
          break;
        }
        case "no-devices": {
          setError(t(msg`That account has no trusted devices to receive the files.`));
          break;
        }
        case "no-files": {
          setError(t(msg`Pick at least one file.`));
          break;
        }
        case "file-too-large": {
          setError(
            `${result.fileName} — ${t(msg`this file is too big for deferred delivery (4 MB max).`)}`,
          );
          break;
        }
        case "too-many-envelopes": {
          setError(t(msg`Too many files for one deferred delivery. Send fewer at a time.`));
          break;
        }
      }
      setPhase("compose");
    })().catch((err: unknown) => {
      setError(errorMessage(err));
      setPhase("compose");
    });
  };

  return (
    <main class="app-screen screen-enter mx-auto w-full max-w-2xl gap-6">
      <PageBackground />
      <TopBar />

      <div class="flex items-center gap-2 text-accent">
        <Clock3 class="h-6 w-6" />
        <h1 class="font-display text-3xl tracking-tight text-foreground">
          {t(msg`Deferred send`)}
        </h1>
      </div>
      <p class="text-sm text-muted-foreground">
        {t(
          msg`Leave files for someone who is offline right now. They are encrypted on this device for the recipient's trusted devices — the server can never read them.`,
        )}
      </p>

      <Show when={error()}>
        {(message) => (
          <p
            data-testid="deferred-error"
            role="alert"
            aria-live="assertive"
            class="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            {message()}
          </p>
        )}
      </Show>

      <Show
        when={phase() !== "done"}
        fallback={
          <section class={panelClass} data-testid="deferred-done">
            <div class="flex items-center gap-2 text-success">
              <Lock class="h-5 w-5" />
              <h2 class="font-display text-xl text-foreground">
                {t(msg`Delivered to the mailbox`)}
              </h2>
            </div>
            <p class="text-sm text-muted-foreground">
              {t(
                msg`Encrypted and waiting. The recipient will find it in their account mailbox the next time they open Enviar.`,
              )}
            </p>
            <div class="flex gap-3">
              <Button
                variant="outline"
                data-testid="btn-deferred-again"
                onClick={() => {
                  setFiles([]);
                  setSentCount(0);
                  setPhase("compose");
                }}
              >
                {t(msg`Send more`)}
              </Button>
              <Link to="/" class="inline-flex">
                <Button variant="ghost">{t(msg`Back home`)}</Button>
              </Link>
            </div>
            <p class="text-xs text-muted-foreground" data-testid="deferred-envelope-count">
              {sentCount()}
            </p>
          </section>
        }
      >
        <section class={panelClass}>
          <label class="flex flex-col gap-2">
            <span class="text-sm font-semibold text-foreground">{t(msg`Recipient's address`)}</span>
            <div class="flex gap-2">
              <input
                class={inputClass}
                data-testid="input-address"
                value={address()}
                onInput={(e) => setAddress(e.currentTarget.value)}
                placeholder={`${ACCOUNT_ADDRESS_PREFIX}…`}
              />
              <Button
                variant="outline"
                data-testid="btn-lookup"
                disabled={address().trim() === ""}
                onClick={lookup}
                class="gap-2"
              >
                <Search class="h-4 w-4" />
                {t(msg`Look up`)}
              </Button>
            </div>
          </label>
          <Show when={recipientDevices()}>
            {(devices) => (
              <p class="text-sm text-muted-foreground" data-testid="recipient-devices">
                {t(msg`Recipient found — trusted devices that can collect:`)} {devices().length}
              </p>
            )}
          </Show>

          <label class="flex flex-col gap-2">
            <span class="text-sm font-semibold text-foreground">{t(msg`Files`)}</span>
            <input
              type="file"
              multiple
              data-testid="input-deferred-files"
              class="w-full cursor-pointer rounded-md border border-input bg-background text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-l-md file:border-0 file:bg-accent file:px-4 file:py-2.5 file:text-sm file:font-medium file:text-accent-foreground"
              onChange={(e) => setFiles([...(e.currentTarget.files ?? [])])}
            />
          </label>
          <Show when={files().length > 0}>
            <ul class="flex flex-col gap-1 text-sm text-muted-foreground">
              <For each={files()}>{(file) => <li class="truncate">{file.name}</li>}</For>
            </ul>
          </Show>

          <Button
            data-testid="btn-deposit"
            disabled={phase() === "sending" || files().length === 0 || address().trim() === ""}
            onClick={send}
            class="gap-2"
          >
            <Lock class="h-4 w-4" />
            {phase() === "sending"
              ? t(msg`Encrypting & delivering…`)
              : t(msg`Encrypt & leave in mailbox`)}
          </Button>
        </section>
      </Show>
    </main>
  );
}

export const deferredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deferred",
  component: DeferredPage,
});
